import SaveIcon from '@mui/icons-material/BookmarkBorder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IosShareIcon from '@mui/icons-material/IosShare';
import InfoIcon from '@mui/icons-material/Info';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Box, Button, Card, CardContent, CardMedia, Container, Divider, ImageList, ImageListItem, Typography, useMediaQuery, useTheme, Alert } from '@mui/material';
import ApplyEventDialogComponent from './components/ApplyEventDialogComponent';
import Grid from '@mui/material/Grid2';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import fallBackBannerPhoto from "../../assets/logo_primary.png";
import '../../styles/events.css';
import { theme } from '../../styles/theme';
import { AppBarPadding, BottomPadding, capitalize, handleShare } from '../../utils/helperUtils';
import ExploreMapView from '../components/ExploreMapView';
import PersonIcon from '@mui/icons-material/Person'; // Optional avatar icon
import { fetchPublicEventDetail, TrackUserApplyExternalAuth } from '../../services/eventServices';
import LoadingComponent from '../components/LoadingComponent';
import EventMapComponent from './components/EventMapComponent';
import { useAuth } from '../../context/AuthContext';

function formatDate(dateString, formatOptions) {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date)) return null;
    return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
}

const getInitials = (firstName, lastName) => {
    if (firstName || lastName) {
        const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : "";
        const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : "";
        return `${firstInitial}${lastInitial}`;
    }
    return "N/A";
};

function EventView() {

    const { slug } = useParams();

    const { userInfo, isLoading, login, logout, isAuthenticated } = useAuth();

    const navigate = useNavigate();
    const location = useLocation();

    const themeX = useTheme();
    const isMobile = useMediaQuery(themeX.breakpoints.down('sm'));

    const [detailsOpen, setDetailsOpen] = useState(true);
    const [descriptionOpen, setDescriptionOpen] = useState(true);

    const [eventInfo, setEventInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedBoothCostBreakdown, setSelectedBoothCostBreakdown] = useState(null);
    const handleDialogOpen = () => setDialogOpen(true);
    const handleDialogClose = () => setDialogOpen(false);

    useEffect(() => {
        const fetchEvent = async () => {
            setLoading(true);
            try {
                const response = await fetchPublicEventDetail(slug, login, logout);

                if (response.success) {
                    setEventInfo(response.data);
                } else {
                    console.error("API Error:", response.error);
                    setError(response.error || "Failed to fetch event details.");
                }
            } catch (err) {
                console.error("Unexpected Error:", err);
                setError(err.message || "Something went wrong while fetching the event.");
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchEvent();
        }
    }, [slug]);

    if (loading || isLoading) {
        return (
            <LoadingComponent />
        );
    }

    if (error) {
        return (
            <Box textAlign="center" mt={4} className="box-container">
                <AppBarPadding />
                <Typography color="error" variant="h6">⚠️ {error || 'N/A'}</Typography>
                <BottomPadding />
            </Box>
        );
    }

    const startTimeFormatted = formatDate(eventInfo?.start_time, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' }) || "Date not available";
    const eventName = eventInfo?.name || "Untitled Event";
    const shortDescription = eventInfo?.short_description || "No description available.";

    const startTimeDetail = formatDate(eventInfo?.start_time, { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }) || "Not specified";
    const endTimeDetail = formatDate(eventInfo?.end_time, { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }) || "Not specified";
    const applicationDeadlineDetail = formatDate(eventInfo?.application_deadline, { day: 'numeric', month: 'long', year: 'numeric' }) || "Not specified";

    const addressObj = eventInfo?.address || {};
    const addressParts = [
        addressObj.line1,
        addressObj.line2,
        addressObj.city,
        addressObj.state,
        addressObj.zip_code,
    ].filter(Boolean);
    const formattedAddress = addressParts.length > 0 ? addressParts.join(', ') : "Address not available";

    const bannerImage = eventInfo?.banner_photo || fallBackBannerPhoto;

    return (
        <Box bgcolor={theme.colors.events.secondary}>
            <AppBarPadding />
            <Container maxWidth="lg">
                {eventInfo?.status && eventInfo.status !== "published" && (
                    <Box mb={2}>
                        <Alert severity="error" sx={{ borderRadius: 2 }}>
                            This event is currently <strong>{eventInfo.status.toUpperCase()}</strong> and is not publicly visible.
                        </Alert>
                    </Box>
                )}
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }} mt={1}>
                        <Card
                            elevation={3}
                            sx={{ borderRadius: 3, }}>
                            <CardMedia
                                component="img"
                                height="150"
                                image={bannerImage}
                                alt="Event Banner"
                                sx={{ objectFit: 'contain' }}
                            />
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12 }} px={1} >
                        <Typography
                            variant={isMobile ? 'subtitle1' : 'h6'}
                            fontWeight={500}
                            sx={{
                                color: theme.colors.events.primaryDark,
                                pb: 1
                            }}
                        >
                            {startTimeFormatted}
                        </Typography>
                        <Typography
                            variant={isMobile ? 'h5' : 'h4'}
                            fontWeight={800}
                            gutterBottom
                            sx={{
                                color: theme.colors.events.primaryDark,
                                textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
                                letterSpacing: 1,
                                pb: 1

                            }}
                        >
                            {eventName}
                        </Typography>
                        <Typography
                            variant={'h6'}
                            textAlign={'center'}
                            jus
                            sx={{ color: theme.colors.events.primaryDark }}>
                            {shortDescription}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        {/* <Box sx={{
                            height: { xs: 200, sm: 450, md: 600 },
                            overflow: "hidden",
                            borderRadius: 2,
                            position: "relative",
                            bgcolor: theme.colors.events.primary,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            color: theme.colors.events.secondary
                        }}>
                            <Typography>No Map View to save credits</Typography>
                        </Box> */}
                        <EventMapComponent coordinates={eventInfo?.location?.coordinates ? { lat: eventInfo.location.coordinates[1], lng: eventInfo.location.coordinates[0] } : undefined} height={{ xs: 200, sm: 250, md: 300 }} />
                    </Grid>
                    <Grid container size={{ xs: 12 }} spacing={1} >
                        <Grid size={{ xs: 10, md: 11, sm: 10 }}>
                            {!isAuthenticated ? (
                                <Button
                                    variant="contained"
                                    fullWidth
                                    // color={theme.colors.primary}
                                    size="large"
                                    sx={{ borderRadius: 4, bgcolor: theme.colors.primary }}
                                    onClick={() => navigate('/_/login', {state: { from: location }})}
                                >
                                    Login / Sign Up to Apply
                                </Button>
                            ) : (
                                eventInfo?.payment_mode === "cash" ? (
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        color="secondary"
                                        size="large"
                                        sx={{
                                            borderRadius: 4,
                                            borderStyle: 'dashed',
                                            borderColor: theme.colors.primary,
                                            color: theme.colors.events.primary,
                                            backgroundColor: "transparent"
                                        }}
                                        onClick={() => {
                                            if (eventInfo?.website) {
                                                TrackUserApplyExternalAuth(slug, login, logout);
                                                window.open(eventInfo.website, '_blank');
                                            }
                                        }}
                                    >
                                        Open Application Link
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        color="eventsPrimary"
                                        size="large"
                                        endIcon={<>✅</>}
                                        sx={{ borderRadius: 4 }}
                                        onClick={handleDialogOpen}
                                        disabled={!userInfo?.is_vendor
                                            || eventInfo?.status !== "published"
                                            || userInfo?.email == eventInfo?.host_details.email
                                        }
                                    >
                                        Apply to Event
                                    </Button>
                                )
                            )}
                        </Grid>
                        {/* <Grid size={{ xs: 2.5, md: 1, sm: 2 }}>
                            <Button variant="contained" size='large'
                                sx={{ borderRadius: 5 }}
                                color="eventsPrimaryDark"
                                fullWidth
                            >
                                <SaveIcon sx={{ color: theme.colors.events.secondary }} />
                            </Button>
                        </Grid> */}
                        <Grid size={{ xs: 2, md: 1, sm: 2 }}>
                            <Button variant="contained" size='large'
                                sx={{ borderRadius: 5 }}
                                color="secondary"
                                fullWidth
                                onClick={() => handleShare("Check out this event!", "Discover this amazing event on Sidewalk!")}

                            >
                                <IosShareIcon sx={{ color: theme.colors.events.secondary }} />
                            </Button>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            {eventInfo?.payment_mode === "cash" ? (
                                <Alert severity="info" sx={{ borderRadius: 4 }}>
                                    This event uses an external application. Tap “Open Application Link” to apply on the host’s site.
                                </Alert>
                            ) : !userInfo?.is_vendor ? (
                                <Alert severity="warning" sx={{ borderRadius: 4 }}>
                                    To apply through Sidewalk, create a storefront first. Tap ☰ menu to get started. For some users, try logging out and back in.
                                </Alert>
                            ) : null}
                        </Grid>


                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Accordion
                            square={true}
                            expanded={detailsOpen}
                            onChange={() => setDetailsOpen(!detailsOpen)}
                            elevation={0}
                            sx={{
                                borderRadius: 5,
                                border: "2px solid #e0e0e0",
                                "&:before": { display: "none" }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: theme.colors.events.primary }} className='manage-card-icon-circle' />}
                            >
                                <Typography variant="h6"
                                    color={theme.colors.events.primaryDark}>
                                    Event Details
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ mt: -1 }}>
                                <Divider sx={{ borderBottomWidth: 2, bgcolor: theme.colors.events.secondary, mb: 2 }} />
                                <Typography variant="body1" >Start Time</Typography>
                                <Typography variant="h6" gutterBottom>{startTimeDetail}</Typography>

                                <Typography variant="body1" >End Time</Typography>
                                <Typography variant="h6" gutterBottom>{endTimeDetail}</Typography>

                                <Typography variant="body1" >Application Deadline</Typography>
                                <Typography variant="h6" gutterBottom>{applicationDeadlineDetail}</Typography>

                                <Typography variant="body1" >Address</Typography>
                                <Typography variant="h6">{formattedAddress}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                    {/* <Grid size={{ xs: 12 }} container>
                        <ImageList
                            cols={isMobile? 3: 4}
                            gap={8}
                            sx={{
                                width: "100%",
                                maxWidth: {xs: 600, sm: 700},
                                margin: "auto",
                                px: 0.5
                            }}
                        >
                            {Array.from({ length: 6 }).map((_, index) => (
                                <ImageListItem key={index} sx={{ position: "relative" }}>
                                    <Box
                                        component="img"
                                        srcSet={`https://picsum.photos/200?random=${Math.floor(Math.random() * 10000)}`}
                                        src={`https://picsum.photos/200?random=${Math.floor(Math.random() * 10000)}`}
                                        alt={`image-${index}`}
                                        loading="lazy"
                                        sx={{
                                            width: "100%",
                                            aspectRatio: "1 / 1",
                                            objectFit: "contain",
                                            borderRadius: 2,
                                            border: "1px solid #e0e0e0"
                                        }}
                                    // onClick={() => handleSelectedImageOpen(index)} 
                                    />
                                </ImageListItem>
                            ))}
                        </ImageList>
                    </Grid> */}
                    <Grid size={{ xs: 12 }}>
                        <Accordion
                            square={true}
                            expanded={descriptionOpen}
                            onChange={() => setDescriptionOpen(!descriptionOpen)}
                            elevation={0}
                            sx={{
                                borderRadius: 5,
                                border: "2px solid #e0e0e0",
                                "&:before": { display: "none" }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: theme.colors.events.primary }} className='manage-card-icon-circle' />}
                            >
                                <Typography variant="h6"
                                    color={theme.colors.events.primaryDark}>
                                    Event Description
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ mt: -1 }}>
                                <Divider sx={{ borderBottomWidth: 2, bgcolor: theme.colors.events.secondary, mb: 2 }} />
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: theme.colors.events.primaryDark,
                                        whiteSpace: "pre-line",    // 💥 This preserves newlines
                                        textAlign: 'left',
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {eventInfo?.description || "No Description Added"}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                    {/* Booth Pricing Accordion */}
                    <Grid size={{ xs: 12 }}>
                        <Accordion
                            square={true}
                            elevation={0}
                            sx={{
                                borderRadius: 5,
                                border: "2px solid #e0e0e0",
                                "&:before": { display: "none" },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: theme.colors.events.primary }} className='manage-card-icon-circle' />}
                            >
                                <Typography variant="h6" color={theme.colors.events.primaryDark}>
                                    Booth Pricing
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ mt: -1 }}>
                                <Divider sx={{ borderBottomWidth: 2, bgcolor: theme.colors.events.secondary, mb: 2 }} />
                                {eventInfo?.booth_types?.length > 0 ? (
                                    eventInfo.booth_types.map((booth, index) => (
                                        <React.Fragment key={index}>
                                            {index > 0 && <Divider sx={{ my: 1 }} />}
                                            <Typography>
                                                <b>Name:</b> {booth.name}<br />
                                                <b>Description:</b> {booth.description}<br />
                                                {/* <b>Quantity:</b> {booth.quantity_available} */}
                                                {eventInfo?.payment_mode !== "cash" && (
                                                    <> <b>Available Spots:</b> {booth.quantity_available - booth.booths_sold}</>
                                                )}<br />
                                                <b>Cost:</b> ${eventInfo?.payment_mode == "cash" ? booth.cost : booth.total_cost} 💸
                                                {eventInfo?.payment_mode !== "cash" &&
                                                    <IconButton
                                                        size="small"
                                                        sx={{ ml: 1, verticalAlign: 'middle' }}
                                                        onClick={() => setSelectedBoothCostBreakdown(booth)}
                                                    >
                                                        <InfoIcon fontSize="small" />
                                                    </IconButton>
                                                }
                                            </Typography>
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        Pricing information not available.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ borderBottomWidth: 2, bgcolor: theme.colors.events.secondary, mb: 1 }} />
                        <Typography
                            variant={isMobile ? 'h6' : 'h5'}
                            fontWeight={700}
                            gutterBottom
                            sx={{
                                color: theme.colors.events.primaryDark,
                                // textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
                                letterSpacing: 1,
                                lineHeight: 1.3,
                                px: 1,
                                pb: 1
                            }}
                        >
                            Organized By
                        </Typography>
                        <Card
                            sx={{
                                borderRadius: 4,
                                px: 2,
                                py: 1.5,
                                bgcolor: '#f9f9f9',
                                border: '1px solid #e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                            }}
                            elevation={0}
                        >
                            <Avatar
                                sx={{
                                    bgcolor: theme.colors.events.primary,
                                    width: 40,
                                    height: 40,
                                    fontWeight: 'bold',
                                    fontSize: 20,
                                }}
                            >
                                {getInitials(eventInfo?.host_details?.first_name, eventInfo?.host_details?.last_name)}
                            </Avatar>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={600} color={theme.colors.events.primaryDark}>
                                    {(eventInfo?.host_details?.first_name || eventInfo?.host_details?.last_name)
                                        ? `${capitalize(eventInfo?.host_details?.first_name)} ${capitalize(eventInfo?.host_details?.last_name)}`.trim()
                                        : "N/A"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {eventInfo?.host_details?.email || "Email Unavailable"}
                                </Typography>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
                {/* Dialog for Apply to Event */}
                <ApplyEventDialogComponent open={dialogOpen} onClose={handleDialogClose} eventInfo={eventInfo} />
                {/* Booth Cost Breakdown Dialog */}
                <Dialog open={!!selectedBoothCostBreakdown} onClose={() => setSelectedBoothCostBreakdown(null)}>
                    <DialogTitle >
                        Cost Breakdown
                    </DialogTitle>
                    <DialogContent dividers>
                        {selectedBoothCostBreakdown && (
                            <Box display="flex" flexDirection="column" gap={2}>
                                <Box display="flex" justifyContent="space-between" gap={1}>
                                    <Typography variant="subtitle1" fontWeight="bold">Base Cost</Typography>
                                    <Typography variant="subtitle1">${selectedBoothCostBreakdown.cost}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" gap={1}>
                                    <Typography variant="subtitle1" fontWeight="bold">Platform & Payment Fees</Typography>
                                    <Typography variant="subtitle1">${selectedBoothCostBreakdown.platform_charge}</Typography>
                                </Box>
                                <Divider />
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="h6" fontWeight="bold">Total Cost</Typography>
                                    <Typography variant="h6">${selectedBoothCostBreakdown.total_cost}</Typography>
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setSelectedBoothCostBreakdown(null)} color="primary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
            <BottomPadding />
        </Box>
    )
}

export default EventView